import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X, Trash2 } from "lucide-react";

export default function StatusViewer({ group, apiBase, onClose, onDelete }) {
  const [idx, setIdx] = useState(0);
  const [videoProgress, setVideoProgress] = useState(0);
  const items = group.items || [];
  const item = items[idx];

  useEffect(() => {
    setVideoProgress(0);
  }, [idx]);

  useEffect(() => {
    if (!item) return;
    if (item.media_type === "video") return; // videos advance on 'ended'
    const t = setTimeout(() => {
      if (idx < items.length - 1) setIdx((i) => i + 1);
      else onClose();
    }, 5000);
    return () => clearTimeout(t);
  }, [idx, item, items.length, onClose]);

  if (!item) return null;

  const next = () => {
    if (idx < items.length - 1) setIdx((i) => i + 1);
    else onClose();
  };
  const prev = () => setIdx((i) => Math.max(0, i - 1));

  const barWidth = (i) => {
    if (i < idx) return "100%";
    if (i > idx) return "0%";
    if (item.media_type === "video") return `${Math.round(videoProgress * 100)}%`;
    return "100%";
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col" data-testid="status-viewer">
      <div className="flex gap-1 p-3 pt-4" style={{ paddingTop: "calc(1rem + env(safe-area-inset-top))" }}>
        {items.map((it, i) => {
          const isActiveImage = i === idx && item.media_type !== "video";
          return (
            <div key={i} className="flex-1 h-1 rounded-full bg-white/25 overflow-hidden">
              <div
                data-testid={i === idx ? "status-progress-active" : undefined}
                className={`h-full bg-white ${isActiveImage ? "animate-[grow_5s_linear]" : ""}`}
                style={isActiveImage ? undefined : { width: barWidth(i), transition: i === idx ? "width 0.15s linear" : "none" }}
              />
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 px-4 pb-2">
        <img src={group.user.picture} alt="" className="h-9 w-9 rounded-full border border-white/20 object-cover bg-[#1A1A1A]" />
        <span className="font-medium text-white flex-1">{group.mine ? "Mon statut" : group.user.name}</span>
        {group.mine && (
          <button
            data-testid="delete-status-button"
            onClick={() => onDelete(item.id)}
            className="p-2 rounded-full text-white/70 hover:text-red-400 hover:bg-white/10 transition"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        )}
        <button data-testid="status-close-button" onClick={onClose} className="p-2 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition">
          <X className="h-6 w-6" />
        </button>
      </div>

      <div className="flex-1 relative flex items-center justify-center overflow-hidden">
        {item.media_type === "video" ? (
          <video
            key={item.id}
            src={`${apiBase}/files/${item.file_path}`}
            autoPlay
            playsInline
            controls={false}
            onEnded={next}
            onTimeUpdate={(e) => {
              const d = e.target.duration;
              if (d && isFinite(d)) setVideoProgress(e.target.currentTime / d);
            }}
            className="max-h-full max-w-full object-contain"
            data-testid="status-video"
          />
        ) : (
          <motion.img
            key={item.id}
            initial={{ opacity: 0.4, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            src={`${apiBase}/files/${item.file_path}`}
            alt="statut"
            className="max-h-full max-w-full object-contain"
          />
        )}
        <button className="absolute left-0 top-0 h-full w-1/3" onClick={prev} aria-label="précédent" />
        <button className="absolute right-0 top-0 h-full w-2/3" onClick={next} aria-label="suivant" />
      </div>
    </div>
  );
}
